import React, { useState, useEffect } from "react";
import {
  Button,
  TableRow,
  Table,
  TableHead,
  TableBody,
  TableCell, TableContainer,
  Paper
} from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";
import { Grid } from "@material-ui/core";
import Widget from "../../components/Widget/Widget";
import PageTitle from "../../components/PageTitle/PageTitle";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import StudentService from "./Locality/Service/studentService";
import CursiveMasterService from "./Locality/Service/cursiveMasterService";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#0f3b62ad",
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  tableOverflow: {
    overflowX: "auto",
  },
}));

export default function Cursive() {
  const classes = useStyles();
  const [studentList, setStudentList] = useState([]);
  const [cursives, setCursives] = useState([]);
  const [tableHeaders, setTableHeaders] = useState(["Student Name"]);
  const [pg, setPg] = useState(0);
  const [rpg, setRpg] = useState(5);

  const handleChangePage = (event, newPage) => {
    setPg(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRpg(parseInt(event.target.value, 10));
    setPg(0);
  };

  // Fetch cursives list from API and combine with existing student cursives
  const getCursivesList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const body = {
      schooleId: userDetails.schooleId || "",
    };

    CursiveMasterService.findCursiveMasterList(body)
      .then((res) => {
        // Extract unique cursive names from CursiveMaster
        const masterCursiveNames = [...new Set(
          res
            .map(item => item.name)
            .filter(name => name) // Remove empty/null names
        )];
        
        // Get existing cursives from students
        const getExistingStudentCursives = () => {
          const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
          const getSpeList = { classId: userDetails.classId };
          
          return StudentService.getAllStudentById(userDetails.schooleId, getSpeList)
            .then((studentRes) => {
              // Collect all cursive keys from all students
              const allStudentCursives = new Set();
              studentRes.forEach((student) => {
                if (student.cursives && typeof student.cursives === 'object') {
                  Object.keys(student.cursives).forEach(key => {
                    if (key) allStudentCursives.add(key);
                  });
                }
              });
              return Array.from(allStudentCursives);
            })
            .catch((err) => {
              console.error("Error fetching existing student cursives:", err);
              return [];
            });
        };

        // Combine master cursives with existing student cursives
        getExistingStudentCursives().then((existingCursives) => {
          // Merge both arrays and remove duplicates
          const combinedCursives = [...new Set([...masterCursiveNames, ...existingCursives])];
          
          // Sort cursives alphabetically
          combinedCursives.sort();
          
          setCursives(combinedCursives);
          // Update table headers dynamically
          setTableHeaders(["Student Name", ...combinedCursives]);
        });
      })
      .catch((err) => {
        console.error("Error fetching cursives list:", err);
        // Fallback: try to get existing cursives from students
        const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
        const getSpeList = { classId: userDetails.classId };
        StudentService.getAllStudentById(userDetails.schooleId, getSpeList)
          .then((studentRes) => {
            const allStudentCursives = new Set();
            studentRes.forEach((student) => {
              if (student.cursives && typeof student.cursives === 'object') {
                Object.keys(student.cursives).forEach(key => {
                  if (key) allStudentCursives.add(key);
                });
              }
            });
            const existingCursives = Array.from(allStudentCursives).sort();
            setCursives(existingCursives);
            setTableHeaders(["Student Name", ...existingCursives]);
          })
          .catch((studentErr) => {
            setCursives([]);
            setTableHeaders(["Student Name"]);
          });
      });
  };

  const getStudentList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const getSpeList = { classId: userDetails.classId };

    StudentService.getAllStudentById(userDetails.schooleId, getSpeList)
      .then((res) => {
        const studentDetails = res.map((student) => {
          // Ensure each student has a cursives object; initialize if missing
          const cursivesObj = student.cursives || {};
          cursives.forEach((cursive) => {
            if (!(cursive in cursivesObj)) {
              cursivesObj[cursive] = false; // Initialize cursive as unchecked
            }
          });
          return {
            _id: student._id,
            studentName: `${student.firstName} ${student.lastName}`,
            cursives: cursivesObj,
          };
        });
        setStudentList(studentDetails);
      })
      .catch((err) => {
        console.error("Error fetching student list:", err);
      });
  };

  const handleCheck = (student, cursiveKey) => {
    // Get the current checked state
    const currentValue = student.cursives[cursiveKey] || false;
    const newValue = !currentValue;
    
    // Ensure all cursives from master list are included in the update
    const cursivesObj = { ...student.cursives };
    // Initialize all cursives from master list if not present
    cursives.forEach((cursive) => {
      if (!(cursive in cursivesObj)) {
        cursivesObj[cursive] = false;
      }
    });
    // Update the specific cursive that was checked/unchecked
    cursivesObj[cursiveKey] = newValue;

    // Update the student's cursive locally
    const updatedStudent = {
      ...student,
      cursives: cursivesObj,
    };

    // Update the student list state immediately for better UX
    const updatedList = studentList.map((s) =>
      s._id === student._id ? updatedStudent : s
    );
    setStudentList(updatedList);

    // Call API to update the student
    StudentService.upadeStudent(updatedStudent)
      .then((res) => {
        // Don't refetch immediately - keep the local state
        // The state is already updated above, so no need to refetch
        console.log("Student cursive updated successfully");
      })
      .catch((err) => {
        console.error(err.response?.data?.message || "Error updating student");
        // On error, revert the local state by refetching
        getStudentList();
      });
  };

  useEffect(() => {
    // Fetch cursives list first, then fetch students
    getCursivesList();
  }, []);

  useEffect(() => {
    // Fetch students when cursives are loaded
    if (cursives.length > 0) {
      getStudentList();
    }
    return () => {
      setStudentList([]);
    };
  }, [cursives]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={10}>
          <PageTitle title="Cursives" />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget
            title=""
            upperTitle
            noBodyPadding
            bodyClass={classes.tableOverflow}
          >
            <TableContainer component={Paper} style={{ maxHeight: 500 }}>
              <Table stickyHeader className="mb-0">
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((key) => (
                      <StyledTableCell key={key}>{key}</StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentList
                    .slice(pg * rpg, pg * rpg + rpg)
                    .map((student) => (
                      <TableRow key={student._id}>
                        <TableCell className="pl-3 fw-normal">
                          {student.studentName}
                        </TableCell>
                        {cursives.map((cursiveKey) => (
                          <TableCell key={cursiveKey} className="pl-3 fw-normal">
                            <input
                              type="checkbox"
                              checked={student.cursives[cursiveKey] || false}
                              style={{ height: "15px", width: "15px" }}
                              onChange={() => handleCheck(student, cursiveKey)}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 50, 100, 500, 1000]}
              count={studentList.length}
              page={pg}
              onPageChange={handleChangePage}
              rowsPerPage={rpg}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Widget>
        </Grid>
      </Grid>
    </>
  );
}

